import { useState, type KeyboardEvent } from "react";
import { IconDeviceFloppy, IconEye, IconEyeOff, IconRestore } from "@tabler/icons-react";
import dayjs from "dayjs";
import { DatePickerInput, Group, IconAction, Input, NumberInput, Stack, Switch, Td, Text, Tr } from "@/components/ui";
import type { Configuracion } from "@/lib/types";
import {
  formatIso,
  formatIsoDateTime,
  parseIso,
  parseIsoDateTime,
} from "@/features/datos-generales/utils/iso-date";
import {
  useEditarConfiguracion,
  useRestablecerConfiguracion,
} from "../hooks/use-configuracion-mutations";
import { ProveedoresConfigPicker } from "./ProveedoresConfigPicker";

/** Clave corta (sin el prefijo de categoría) para la columna. */
function claveCorta(clave: string): string {
  const i = clave.indexOf(":");
  return i >= 0 ? clave.slice(i + 1) : clave;
}

/** Versión legible del valor por defecto según el tipo (para la columna "Por defecto"). */
function formatoLegible(valor: string | null, tipo: Configuracion["tipo"]): string {
  if (!valor) return "—";
  switch (tipo) {
    case "Secreto":
      return "••••••••"; // nunca se revela el valor por defecto de un secreto.
    case "Booleano":
      return valor.toLowerCase() === "true" ? "Sí" : "No";
    case "Fecha": {
      const d = parseIso(valor);
      return d ? dayjs(d).format("DD/MM/YYYY") : valor;
    }
    case "FechaHora": {
      const d = parseIsoDateTime(valor);
      return d ? dayjs(d).format("DD/MM/YYYY HH:mm") : valor;
    }
    default:
      return valor;
  }
}

/** Input de contraseña con ícono de revelar/ocultar · no hay átomo `PasswordInput` compartido (único consumidor). */
function SecretoInput({
  value,
  onChange,
  onKeyDown,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
  placeholder: string;
}) {
  const [visible, setVisible] = useState(false);
  return (
    <Input
      size="xs"
      type={visible ? "text" : "password"}
      value={value}
      onChange={(e) => onChange(e.currentTarget.value)}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      rightSection={
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? "Ocultar valor" : "Mostrar valor"}
          className="cursor-pointer text-muted-foreground hover:text-foreground"
        >
          {visible ? <IconEyeOff size={14} /> : <IconEye size={14} />}
        </button>
      }
    />
  );
}

/** Fecha + hora · `DatePickerInput` (solo fecha) + `<input type="time">` combinados · no hay átomo `DateTimePicker` compartido (único consumidor). */
function FechaHoraInput({
  value,
  onChange,
  placeholder,
}: {
  value: Date | null;
  onChange: (d: Date | null) => void;
  placeholder: string;
}) {
  const hora = value ? dayjs(value).format("HH:mm") : "";
  return (
    <div className="flex gap-1.5">
      <DatePickerInput
        size="xs"
        clearable
        wrapperClassName="flex-1"
        value={value}
        placeholder={placeholder}
        onChange={(d) => {
          if (!d) {
            onChange(null);
            return;
          }
          const merged = new Date(d);
          if (value) merged.setHours(value.getHours(), value.getMinutes());
          onChange(merged);
        }}
      />
      <input
        type="time"
        value={hora}
        disabled={!value}
        onChange={(e) => {
          if (!value) return;
          const [h, m] = e.currentTarget.value.split(":").map(Number);
          const merged = new Date(value);
          merged.setHours(h || 0, m || 0);
          onChange(merged);
        }}
        className="h-7 w-24 rounded-md border border-input bg-background px-2 text-xs disabled:opacity-50"
      />
    </div>
  );
}

export function ConfiguracionRow({ cfg }: { cfg: Configuracion }) {
  const [valor, setValor] = useState(cfg.valor);
  const editar = useEditarConfiguracion();
  const restablecer = useRestablecerConfiguracion();

  const cambiado = valor !== cfg.valor;
  const esDefault = (cfg.valorDefault ?? "") === cfg.valor;
  const guardar = () => editar.mutate({ clave: cfg.clave, valor });
  const enterGuarda = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && cambiado) guardar();
  };

  return (
    <Tr>
      <Td>
        <Stack gap={2} className="min-w-0">
          <Text size="sm" className="font-mono" fw={500}>{claveCorta(cfg.clave)}</Text>
          {cfg.descripcion && (
            // Contraste subido (antes muted-foreground ~4.5:1 se veía tenue en xs):
            // ink-600 en claro (~7:1) / ink-400 en oscuro (igual que el muted actual).
            <Text size="xs" className="text-ink-600 dark:text-ink-400">{cfg.descripcion}</Text>
          )}
        </Stack>
      </Td>
      <Td>
        {cfg.tipo === "Entero" || cfg.tipo === "Decimal" ? (
          <NumberInput
            size="xs"
            value={valor === "" ? "" : Number(valor)}
            onChange={(v) => setValor(v === "" ? "" : String(v))}
            onKeyDown={enterGuarda}
            placeholder={cfg.valorDefault ?? ""}
          />
        ) : cfg.tipo === "Booleano" ? (
          <Switch
            checked={valor.trim().toLowerCase() === "true"}
            onCheckedChange={(checked) => setValor(checked ? "true" : "false")}
          />
        ) : cfg.tipo === "Fecha" ? (
          <DatePickerInput
            size="xs"
            clearable
            value={parseIso(valor)}
            onChange={(d) => setValor(formatIso(d))}
            placeholder={cfg.valorDefault ? formatoLegible(cfg.valorDefault, cfg.tipo) : "Sin fecha"}
          />
        ) : cfg.tipo === "FechaHora" ? (
          <FechaHoraInput
            value={parseIsoDateTime(valor)}
            onChange={(d) => setValor(formatIsoDateTime(d))}
            placeholder={cfg.valorDefault ? formatoLegible(cfg.valorDefault, cfg.tipo) : "Sin fecha/hora"}
          />
        ) : cfg.tipo === "ListaProveedores" ? (
          <ProveedoresConfigPicker value={valor} onChange={setValor} />
        ) : cfg.tipo === "Secreto" ? (
          <SecretoInput
            value={valor}
            onChange={setValor}
            onKeyDown={enterGuarda}
            placeholder={cfg.valorDefault ? "••••••••" : ""}
          />
        ) : (
          <Input
            size="xs"
            value={valor}
            onChange={(e) => setValor(e.currentTarget.value)}
            onKeyDown={enterGuarda}
            placeholder={cfg.valorDefault ?? ""}
          />
        )}
      </Td>
      <Td>
        <Text size="xs" className="font-mono text-ink-600 dark:text-ink-400">{formatoLegible(cfg.valorDefault, cfg.tipo)}</Text>
      </Td>
      <Td align="right">
        <Group gap={4} className="flex-nowrap justify-end">
          <IconAction label="Guardar" size="sm" disabled={!cambiado} loading={editar.isPending} onClick={guardar}>
            <IconDeviceFloppy size={16} />
          </IconAction>
          {/* Una lista de proveedores no se restablece (se vaciaría) · decisión del usuario. */}
          {cfg.tipo !== "ListaProveedores" && (
            <IconAction
              label="Restablecer al valor por defecto"
              size="sm"
              disabled={esDefault && !cambiado}
              loading={restablecer.isPending}
              onClick={() => restablecer.mutate(cfg.clave, { onSuccess: (c) => setValor(c.valor) })}
            >
              <IconRestore size={16} />
            </IconAction>
          )}
        </Group>
      </Td>
    </Tr>
  );
}
